import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Breadcrumbs,
  Link,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  CloudUpload,
  Save,
  ArrowBack,
  Delete,
} from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  createProduct,
  updateProduct,
  getAllFeatures,
  getAllVendorsWithoutPagination,
} from "../../services/Api";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const ModifyProducts = () => {
  const { subCategoryId, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = !!id;

  const subCategoryName = location.state?.subCategoryName || "Subcategory";
  const categoryName = location.state?.categoryName || "Category";
  const categoryId = location.state?.categoryId;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [features, setFeatures] = useState([]);
  const [groceryVendors, setGroceryVendors] = useState([]);
  const [isFeatureMenuOpen, setIsFeatureMenuOpen] = useState(false);

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const res = await getAllFeatures();
        if (res.success) setFeatures(res.data || []);
      } catch (err) {
        console.error("Failed to fetch features", err);
      }
    };
    fetchFeatures();
  }, []);

  useEffect(() => {
    const getGroceryVendors = async () => {
      try {
        const res = await getAllVendorsWithoutPagination();
        if (res.success) setGroceryVendors(res.data || []);
      } catch (err) {
        console.error("Failed to fetch grocery vendors", err);
      }
    };
    getGroceryVendors();
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    information: "",
    highlights: "",
    isActive: true,
    categoryId: categoryId || "",
    subCategoryId: subCategoryId || "",
    price: "",
    discountPrice: "",
    stockQuantity: "",
    featureIds: [],
    productVariant: "",
    vendorId: "",
  });
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  console.log(location.state?.product, "location.state?.product");

  useEffect(() => {
    if (isEdit && location.state?.product) {
      const { product } = location.state;
      setFormData({
        name: product.name || "",
        information: product.information || "",
        highlights: product.highlights || "",
        isActive: product.isActive !== undefined ? product.isActive : true,
        categoryId: categoryId || product.categoryId || "",
        subCategoryId: subCategoryId || product.subCategoryId || "",
        price: product.price || "",
        discountPrice: product.discountPrice || "",
        stockQuantity: product.stockQuantity || "",
        lowStockAlert: product.lowStockAlert || "",
        featureIds: product.features
          ? product.features.map((f) => f.id)
          : product.featureIds || [],
        productVariant: product.productVariant || "",
        vendorId: product.vendorId || "",
      });
      setPreview(
        product.image || (product.productImages && product.productImages[0]),
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, location.state, subCategoryId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuillChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e) => {
    setFormData((prev) => ({ ...prev, isActive: e.target.checked }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    const data = new FormData();
    data.append("name", formData.name);
    data.append("information", formData.information);
    data.append("highlights", formData.highlights);
    data.append("isActive", formData.isActive);
    data.append("categoryId", formData.categoryId);
    data.append("subCategoryId", formData.subCategoryId);
    data.append("price", formData.price);
    data.append("discountPrice", formData.discountPrice);
    data.append("stockQuantity", formData.stockQuantity);
    data.append("lowStockAlert", formData.lowStockAlert);
    data.append("productVariant", formData.productVariant);
    data.append("vendorId", formData.vendorId);

    // Handling featureIds as array
    const featureIdsArray = Array.isArray(formData.featureIds)
      ? formData.featureIds
      : [];
    featureIdsArray.forEach((id) => data.append("featureIds[]", id));

    if (image) {
      data.append("productImages", image);
    }

    if (!formData.vendorId) {
      setError("Please select a grocery vendor.");
      setLoading(false);
      return;
    }

    try {
      let response;
      if (isEdit) {
        response = await updateProduct(id, data);
      } else {
        response = await createProduct(formData.vendorId, data);
      }

      if (response.success) {
        setSuccess(true);
        setTimeout(
          () =>
            navigate(`/sub-categories/${subCategoryId}/products`, {
              state: { subCategoryName, categoryName, categoryId },
            }),
          1500,
        );
      } else {
        setError(response.message || "Failed to save product");
      }
    } catch (err) {
      let msg = err.response?.data?.message || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate(`/sub-categories/${subCategoryId}/products`, {
                state: { subCategoryName, categoryName, categoryId },
              });
            }}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <ArrowBack sx={{ mr: 0.5, fontSize: "inherit" }} />
            {subCategoryName} Products
          </Link>
          <Typography color="text.primary">
            {isEdit ? "Edit Product" : "Create Product"}
          </Typography>
        </Breadcrumbs>
        <Typography variant="h4" sx={{ fontWeight: 300 }}>
          {isEdit ? "Modify Product" : "Create New Product"}
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "text.border",
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Product saved successfully! Redirecting...
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            {/* Image Upload Section */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Product Image
              </Typography>
              <Box
                sx={{
                  border: "2px dashed",
                  borderColor: "text.border",
                  borderRadius: 2,
                  height: 200,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  backgroundColor: "rgba(0,0,0,0.02)",
                  position: "relative",
                }}
              >
                {preview ? (
                  <>
                    <Box
                      component="img"
                      src={preview}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "rgba(255,255,255,0.8)",
                        "&:hover": { backgroundColor: "white" },
                      }}
                      onClick={() => {
                        setImage(null);
                        setPreview(null);
                      }}
                    >
                      <Delete color="error" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <CloudUpload
                      sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Upload Product Image
                    </Typography>
                    <input
                      type="file"
                      accept="image/*"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        opacity: 0,
                        cursor: "pointer",
                      }}
                      onChange={handleImageChange}
                    />
                  </>
                )}
              </Box>
              <input
                id="file-input"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  handleImageChange(e);
                  // Reset value so selecting the same file again triggers onChange
                  e.target.value = null;
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1, textAlign: "center" }}
              >
                Note: Image size must be 300px * 300px
              </Typography>
              <Button
                variant="outlined"
                sx={{ mt: 2, fontSize: "10px !important" }}
                startIcon={<CloudUpload />}
                onClick={() => document.getElementById("file-input").click()}
              >
                Upload Image
              </Button>
            </Grid>

            {/* Details Section */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Product Details
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Product Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g. Fresh Organic Apples"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontWeight: 500 }}
                  >
                    Product Information
                  </Typography>
                  <Box
                    sx={{
                      "& .ql-container": {
                        borderBottomLeftRadius: "8px",
                        borderBottomRightRadius: "8px",
                        minHeight: "150px",
                      },
                      "& .ql-toolbar": {
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                        backgroundColor: "rgba(0,0,0,0.02)",
                      },
                      "& .ql-editor": { fontSize: "0.9rem" },
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      value={formData.information}
                      onChange={(val) => handleQuillChange("information", val)}
                      placeholder="Detailed description of the product..."
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontWeight: 500 }}
                  >
                    Product Highlights
                  </Typography>
                  <Box
                    sx={{
                      "& .ql-container": {
                        borderBottomLeftRadius: "8px",
                        borderBottomRightRadius: "8px",
                        minHeight: "150px",
                      },
                      "& .ql-toolbar": {
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                        backgroundColor: "rgba(0,0,0,0.02)",
                      },
                      "& .ql-editor": { fontSize: "0.9rem" },
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      value={formData.highlights}
                      onChange={(val) => handleQuillChange("highlights", val)}
                      placeholder="Key features (e.g., Organic, High Fiber)..."
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Original Price"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Discount Price"
                    name="discountPrice"
                    value={formData.discountPrice}
                    onChange={handleInputChange}
                  />
                </Grid>
                {/* <Grid size={{ xs: 12, md: 4 }}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Discount (%)"
                                        name="discountPercentage"
                                        value={formData.discountPercentage}
                                        onChange={handleInputChange}
                                    />
                                </Grid> */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Stock Quantity"
                    name="stockQuantity"
                    value={formData.stockQuantity}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    type="number"
                    label="Low Stock Alert"
                    name="lowStockAlert"
                    value={formData.lowStockAlert}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    label="Product Variant"
                    name="productVariant"
                    value={formData.productVariant}
                    onChange={handleInputChange}
                    placeholder="e.g. 500g, 1L, Small"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel id="vendor-label">Grocery Vendor</InputLabel>
                    <Select
                      labelId="vendor-label"
                      name="vendorId"
                      value={formData.vendorId || ""}
                      onChange={handleInputChange}
                      label="Grocery Vendor"
                    >
                      <MenuItem value="">
                        <em>Select Vendor</em>
                      </MenuItem>
                      {groceryVendors.map((vendor) => (
                        <MenuItem key={vendor.id} value={vendor.id}>
                          {vendor.shopName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="features-label">Features</InputLabel>
                    <Select
                      labelId="features-label"
                      multiple
                      name="featureIds"
                      open={isFeatureMenuOpen}
                      onOpen={() => setIsFeatureMenuOpen(true)}
                      onClose={() => setIsFeatureMenuOpen(false)}
                      value={
                        Array.isArray(formData.featureIds)
                          ? formData.featureIds
                          : []
                      }
                      onChange={handleInputChange}
                      input={<OutlinedInput label="Features" />}
                      renderValue={(selected) =>
                        selected
                          .map((value) => {
                            const feature = features.find(
                              (f) => f.id === value,
                            );
                            return feature ? feature.label : value;
                          })
                          .join(", ")
                      }
                    >
                      {features.map((feature) => (
                        <MenuItem key={feature.id} value={feature.id}>
                          <Checkbox
                            size="small"
                            checked={
                              (Array.isArray(formData.featureIds)
                                ? formData.featureIds
                                : []
                              ).indexOf(feature.id) > -1
                            }
                          />
                          <ListItemText
                            sx={{
                              fontSize: "105px !important",
                            }}
                            primary={feature.label}
                          />
                        </MenuItem>
                      ))}
                      <Box
                        sx={{
                          p: 0.5,
                          borderTop: 1,
                          borderColor: "divider",
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsFeatureMenuOpen(false);
                          }}
                        >
                          Done
                        </Button>
                      </Box>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      ml: 1,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={handleSwitchChange}
                          color="primary"
                        />
                      }
                      label={
                        formData.isActive ? "Active on App" : "Hidden from App"
                      }
                    />
                  </Box>
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 5,
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() =>
                    navigate(`/sub-categories/${subCategoryId}/products`, {
                      state: { subCategoryName, categoryName, categoryId },
                    })
                  }
                  disabled={loading}
                  sx={{ px: 4 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Save />
                    )
                  }
                  disabled={loading || (!isEdit && !image)}
                  sx={{ px: 6, backgroundColor: "primary.main" }}
                >
                  {isEdit ? "Update Product" : "Create Product"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default ModifyProducts;
